// Admin overview tab. Top-of-funnel KPIs + a small recent-activity
// slice. Read-only — drilling into anything routes through the Usage
// or Errors tabs. Backed by /api/admin/overview (single round trip).

import { useEffect, useState } from 'react'
import { api, type AdminOverview } from '../api'

export interface AdminOverviewProps {
  /** Called when the user clicks a recent-activity row — forwards to
   *  the Usage tab so the row can be inspected with its full filter
   *  context. The shell wires this to its tab switcher. */
  onSeeUsage: () => void
  /** Same for the unresolved-errors call-out → Errors tab. */
  onSeeErrors: () => void
}

export function AdminOverviewView({ onSeeUsage, onSeeErrors }: AdminOverviewProps) {
  const [data, setData] = useState<AdminOverview | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const d = await api.getAdminOverview()
        if (!cancelled) setData(d)
      } catch (e) {
        if (!cancelled) setError((e as Error).message)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  if (error) {
    return (
      <div
        style={{
          background: 'var(--red-soft)',
          border: '1px solid var(--red)',
          padding: '6px 10px',
          borderRadius: 4,
          color: 'var(--red)',
          fontSize: 12,
        }}
      >
        {error}
      </div>
    )
  }
  if (!data) {
    return <div style={{ color: 'var(--ink-3)', fontSize: 12 }}>loading…</div>
  }

  return (
    <div style={{ fontSize: 13, color: 'var(--ink-1)' }}>
      {/* System counts */}
      <SectionLabel>SYSTEM</SectionLabel>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
          gap: 10,
          marginBottom: 24,
        }}
      >
        <Card label="users" value={fmtNum(data.counts.users)} />
        <Card label="tenants" value={fmtNum(data.counts.tenants)} />
        <Card label="threads" value={fmtNum(data.counts.threads)} />
        <Card label="cards" value={fmtNum(data.counts.cards)} />
      </div>

      {/* 24h rollup */}
      <SectionLabel>LAST 24H</SectionLabel>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
          gap: 10,
          marginBottom: 24,
        }}
      >
        <Card
          label="LLM calls"
          value={fmtNum(data.today.calls)}
          onClick={onSeeUsage}
        />
        <Card label="input tokens" value={fmtTokens(data.today.input_tokens)} />
        <Card
          label="output tokens"
          value={fmtTokens(data.today.output_tokens)}
        />
        <Card
          label="cache read"
          value={fmtTokens(data.today.cache_read_tokens)}
          sub={`+${fmtTokens(data.today.cache_creation_tokens)} write`}
        />
        <Card
          label="errors"
          value={fmtNum(data.today.errors)}
          tone={data.today.errors > 0 ? 'warn' : undefined}
          onClick={onSeeErrors}
        />
        <Card
          label="avg latency"
          value={
            data.today.avg_latency_ms != null
              ? `${Math.round(data.today.avg_latency_ms)}ms`
              : '—'
          }
        />
      </div>

      {/* Unresolved errors callout */}
      {data.errors_unresolved > 0 && (
        <div
          style={{
            border: '1px solid var(--rule-s)',
            background: 'var(--paper-1, #fafafa)',
            borderRadius: 6,
            padding: '10px 14px',
            marginBottom: 24,
            display: 'flex',
            alignItems: 'center',
            gap: 12,
          }}
        >
          <span style={{ fontSize: 16 }}>⚠</span>
          <span style={{ flex: 1 }}>
            <strong>{data.errors_unresolved}</strong> unresolved error
            {data.errors_unresolved === 1 ? '' : 's'} in the queue
          </span>
          <button
            type="button"
            onClick={onSeeErrors}
            style={{
              background: 'transparent',
              border: '1px solid var(--rule-s)',
              color: 'var(--ink-2)',
              padding: '3px 10px',
              borderRadius: 4,
              cursor: 'pointer',
              fontSize: 12,
            }}
          >
            triage →
          </button>
        </div>
      )}

      {/* Recent calls */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          marginBottom: 8,
        }}
      >
        <SectionLabel inline>RECENT LLM CALLS</SectionLabel>
        <button
          type="button"
          onClick={onSeeUsage}
          style={{
            marginLeft: 'auto',
            background: 'transparent',
            border: '1px solid var(--rule-s)',
            color: 'var(--ink-2)',
            padding: '3px 10px',
            borderRadius: 4,
            cursor: 'pointer',
            fontSize: 12,
          }}
        >
          see all →
        </button>
      </div>
      {data.recent_calls.length === 0 ? (
        <div style={{ color: 'var(--ink-3)', fontSize: 12 }}>
          no calls yet
        </div>
      ) : (
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: 12,
          }}
        >
          <thead>
            <tr
              style={{
                borderBottom: '1px solid var(--rule-s)',
                textAlign: 'left',
              }}
            >
              <th style={th}>when</th>
              <th style={th}>task</th>
              <th style={th}>tier</th>
              <th style={thNum}>in / out</th>
              <th style={thNum}>latency</th>
              <th style={th}>status</th>
            </tr>
          </thead>
          <tbody>
            {data.recent_calls.map((r) => {
              const ts = new Date(r.created_at)
              return (
                <tr key={r.id}>
                  <td style={td}>
                    {ts.toLocaleDateString()} {ts.toLocaleTimeString()}
                  </td>
                  <td style={td}>{r.task}</td>
                  <td style={td}>{r.tier_id ?? '—'}</td>
                  <td style={tdNum}>
                    {fmtTokens(r.input_tokens)} / {fmtTokens(r.output_tokens)}
                  </td>
                  <td style={tdNum}>
                    {r.latency_ms != null ? `${r.latency_ms}ms` : '—'}
                  </td>
                  <td style={td}>
                    <span
                      style={{
                        background: r.status === 'ok' ? '#efe' : '#fee',
                        padding: '1px 6px',
                        borderRadius: 3,
                        fontSize: 11,
                      }}
                    >
                      {r.status}
                    </span>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      )}
    </div>
  )
}

// ── Helpers ────────────────────────────────────────────────────────

function SectionLabel({
  children,
  inline,
}: {
  children: React.ReactNode
  inline?: boolean
}) {
  return (
    <div
      style={{
        color: 'var(--ink-3)',
        fontSize: 11,
        textTransform: 'uppercase',
        letterSpacing: '.04em',
        marginBottom: inline ? 0 : 8,
      }}
    >
      {children}
    </div>
  )
}

function Card({
  label,
  value,
  sub,
  tone,
  onClick,
}: {
  label: string
  value: string
  sub?: string
  tone?: 'good' | 'warn'
  onClick?: () => void
}) {
  const valueColor =
    tone === 'good'
      ? 'var(--green, #2a8a45)'
      : tone === 'warn'
        ? 'var(--red, #c54)'
        : 'var(--ink-1)'
  return (
    <div
      onClick={onClick}
      style={{
        border: '1px solid var(--rule-s)',
        borderRadius: 6,
        padding: '10px 14px',
        background: 'var(--paper-1, #fafafa)',
        cursor: onClick ? 'pointer' : 'default',
      }}
    >
      <div
        style={{
          color: 'var(--ink-3)',
          fontSize: 11,
          textTransform: 'uppercase',
          letterSpacing: '.04em',
          marginBottom: 4,
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: 18, fontWeight: 600, color: valueColor }}>
        {value}
      </div>
      {sub && (
        <div style={{ color: 'var(--ink-3)', fontSize: 11, marginTop: 2 }}>
          {sub}
        </div>
      )}
    </div>
  )
}

function fmtNum(n: number): string {
  return n.toLocaleString()
}

function fmtTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`
  return n.toLocaleString()
}

const th: React.CSSProperties = {
  padding: '6px 8px',
  fontWeight: 500,
  color: 'var(--ink-3)',
  fontSize: 11,
  textTransform: 'uppercase',
  letterSpacing: '.04em',
}

const thNum: React.CSSProperties = { ...th, textAlign: 'right' }

const td: React.CSSProperties = {
  padding: '6px 8px',
  verticalAlign: 'top',
  borderBottom: '1px solid var(--rule-xs, #eee)',
}

const tdNum: React.CSSProperties = {
  ...td,
  textAlign: 'right',
  fontFeatureSettings: '"tnum"',
}
